import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useClient, useOrpc } from '@/hooks/useClient'
import type {
  FrequentList,
  ReferralLocation,
  ReferralSpecialtyCode,
  RosterView,
} from './referralTypes'

// A disabled query still needs a well-typed input; it is never sent.
const IDLE_SPECIALTY: ReferralSpecialtyCode = '207RC0000X'

export type NearbyInput = {
  lat: number
  lon: number
  radiusMiles: 25 | 50
  specialtyCode: ReferralSpecialtyCode
  query?: string
  /** 'state' searches the patient's whole state by clinic name instead of the radius. */
  scope?: 'radius' | 'state'
  state?: string
}

export function useReferralNearby(input: NearbyInput | null) {
  const orpc = useOrpc()
  return useQuery({
    ...orpc.referralDiscoveryNearby.queryOptions({
      input: input ?? { lat: 0, lon: 0, radiusMiles: 25, specialtyCode: IDLE_SPECIALTY },
    }),
    enabled: input !== null,
    staleTime: 60_000,
    retry: 1,
  })
}

export type DoctorsInput = {
  specialtyCode: ReferralSpecialtyCode
  name: string
  state?: string
  lat?: number
  lon?: number
  zip?: string
}

export function useReferralDoctors(input: DoctorsInput | null) {
  const orpc = useOrpc()
  return useQuery({
    ...orpc.referralDiscoveryDoctors.queryOptions({
      input: input ?? { specialtyCode: IDLE_SPECIALTY, name: '--' },
    }),
    enabled: input !== null,
    staleTime: 60_000,
    retry: 1,
  })
}

export type FrequentInput = { specialtyCode: ReferralSpecialtyCode; lat?: number; lon?: number }

export function useReferralFrequent(input: FrequentInput | null) {
  const orpc = useOrpc()
  return useQuery({
    ...orpc.referralDiscoveryFrequent.queryOptions({
      input: input ?? { specialtyCode: IDLE_SPECIALTY },
    }),
    enabled: input !== null,
    staleTime: 30_000,
    retry: 1,
  })
}

export function useResolveRecipient() {
  const orpc = useOrpc()
  return useMutation(orpc.referralDiscoveryResolve.mutationOptions())
}

/** Adds a recipient to the clinic's Frequently used list without a referral. */
export function useRememberRecipient() {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  return useMutation(
    orpc.referralDiscoveryRemember.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: orpc.referralDiscoveryFrequent.key() })
      },
    }),
  )
}

/**
 * Pins or unpins a Frequently used recipient. The toggle flips at once in every cached
 * Frequently used list (whatever its input) and only that row flips back if the save fails;
 * the refetch once the last pending bookmark settles brings the server's bookmarked-first
 * order without overwriting another row's in-flight flip.
 */
export function useBookmarkRecipient() {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  const frequentKey = orpc.referralDiscoveryFrequent.key()
  const setBookmarked = (key: string, bookmarked: boolean) =>
    queryClient.setQueriesData<FrequentList>({ queryKey: frequentKey }, (old) =>
      old
        ? {
            ...old,
            recipients: old.recipients.map((r) => (r.key === key ? { ...r, bookmarked } : r)),
          }
        : old,
    )
  return useMutation(
    orpc.referralDiscoveryBookmark.mutationOptions({
      onMutate: async ({ key, bookmarked }) => {
        await queryClient.cancelQueries({ queryKey: frequentKey })
        setBookmarked(key, bookmarked)
      },
      onError: (_err, { key, bookmarked }) => setBookmarked(key, !bookmarked),
      onSettled: () => {
        // This mutation still counts as pending here; refetch only after the last one.
        const mutationKey = orpc.referralDiscoveryBookmark.mutationKey()
        if (queryClient.isMutating({ mutationKey }) === 1) {
          void queryClient.invalidateQueries({ queryKey: frequentKey })
        }
      },
    }),
  )
}

const POLL_MS = 5_000
const MAX_MS = 240_000

/**
 * Rosters build asynchronously server-side; clinic rows render at once and this fills
 * in each practice's doctors as they land. Polls only the pending ones, every 5 s, for
 * at most 4 minutes per result set (the facility-discovery cadence).
 */
export function useRosterPolling(
  locations: ReferralLocation[],
  specialtyCode: ReferralSpecialtyCode | null,
): Record<string, RosterView> {
  const client = useClient()
  const clientRef = useRef(client)
  clientRef.current = client
  const [views, setViews] = useState<Record<string, RosterView>>({})
  const viewsRef = useRef(views)
  viewsRef.current = views
  const key = `${specialtyCode ?? ''}|${locations.map((l) => l.placeId).join(',')}`

  useEffect(() => {
    setViews({})
    if (!specialtyCode) return
    const started = Date.now()
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined

    const tick = async () => {
      if (cancelled) return
      const stillPending = locations.filter(
        (l) => (viewsRef.current[l.placeId] ?? l.roster).status === 'pending',
      )
      if (stillPending.length === 0) return

      // Time budget exhausted with rosters still pending: stop polling rather than
      // scheduling another tick, and mark each of them 'failed' so the UI falls back
      // to "Doctor list unavailable" instead of a spinner that can never resolve.
      if (Date.now() - started > MAX_MS) {
        if (!cancelled) {
          setViews((prev) => ({
            ...prev,
            ...Object.fromEntries(
              stillPending.map((l) => [
                l.placeId,
                {
                  status: 'failed' as const,
                  doctors: (viewsRef.current[l.placeId] ?? l.roster).doctors,
                },
              ]),
            ),
          }))
        }
        return
      }

      const pending = stillPending.map((l) => l.placeId).slice(0, 20)
      try {
        const { items } = await clientRef.current.referralDiscoveryRosters({
          placeIds: pending,
          specialtyCode,
        })
        if (!cancelled) setViews((prev) => ({ ...prev, ...items }))
      } catch {
        // transient; try again on the next tick
      }
      if (!cancelled) timer = setTimeout(() => void tick(), POLL_MS)
    }
    timer = setTimeout(() => void tick(), POLL_MS)
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
    // `key` stands in for the location set and specialty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  return views
}
